import { animate, stagger } from 'animejs';

/* ============================================================
   rasdefense — hero 3D particle morph + interactions
   A rotating 3D point cloud that morphs over time between three
   models: dense-dot globe → travelling signal waveform → binary cube.
   Inspired by Julian Garnier's anime.js 3D dots demo.
   ============================================================ */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const N = 900;          // particle count
const GOLDEN = Math.PI * (3 - Math.sqrt(5));

/* ---------------- 3D model generators (unit space, |coord| ~ 1) ---------------- */

// 1) GLOBE — even sphere shell via Fibonacci sphere.
function modelGlobe() {
  const pts = [];
  for (let i = 0; i < N; i++) {
    const y = 1 - (i / (N - 1)) * 2;        // -1..1
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const th = GOLDEN * i;
    pts.push([Math.cos(th) * r, y, Math.sin(th) * r]);
  }
  return { pts, links: [] };
}

// 2) WAVE — a grid of dots on the x/z plane; the render loop drives a
//    travelling sine in y, giving a 3D signal-waveform surface.
function modelWave() {
  const pts = [];
  const cols = 38;
  const rows = Math.ceil(N / cols);
  for (let r = 0; r < rows && pts.length < N; r++) {
    for (let c = 0; c < cols && pts.length < N; c++) {
      const x = (c / (cols - 1)) * 2 - 1;       // -1 .. 1
      const z = (r / (rows - 1)) * 1.4 - 0.7;   // -0.7 .. 0.7
      pts.push([x, 0, z]);
    }
  }
  while (pts.length < N) pts.push([0, 0, 0]);
  return { pts: pts.slice(0, N) };
}

// 3) CUBE — hollow cube (6 gridded faces); rendered as 0/1 glyphs.
function modelCube() {
  const pts = [];
  const per = 12, lo = -0.55, hi = 0.55;   // corner ≈ globe radius, so it fits
  const lin = i => lo + (hi - lo) * i / (per - 1);
  const faces = [
    (a, b) => [lin(a), lin(b), lo], (a, b) => [lin(a), lin(b), hi],
    (a, b) => [lin(a), lo, lin(b)], (a, b) => [lin(a), hi, lin(b)],
    (a, b) => [lo, lin(a), lin(b)], (a, b) => [hi, lin(a), lin(b)],
  ];
  for (const f of faces)
    for (let a = 0; a < per; a++)
      for (let b = 0; b < per; b++) pts.push(f(a, b));
  while (pts.length < N) {
    const f = faces[(Math.random() * 6) | 0];
    pts.push(f((Math.random() * per) | 0, (Math.random() * per) | 0));
  }
  return { pts: pts.slice(0, N) };
}

/* ---------------- 3D particle system ---------------- */
function initParticles() {
  const canvas = document.getElementById('hero-canvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  let w, h, R, cx, cy, dpr;

  const MODELS = [modelGlobe(), modelWave(), modelCube()];
  const NAMES = ['globe', 'wave', 'cube'];

  const particles = MODELS[0].pts.map(([x, y, z]) => ({
    x, y, z,                 // current 3D position (anime.js animates these)
    baseR: 0.8 + Math.random() * 1.4,
    gold: Math.random() < 0.3,
    bit: Math.random() < 0.5 ? '0' : '1',   // glyph for the binary-cube model
  }));

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = canvas.clientWidth; h = canvas.clientHeight;
    canvas.width = w * dpr; canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    R = Math.min(Math.min(w, h) * 0.34, w * 0.32);
    cx = w * (w > 760 ? 0.66 : 0.5);
    cy = h * 0.5;
  }
  resize();
  window.addEventListener('resize', resize);

  const PERSP = 2.6;       // perspective distance (unit space)
  let t = 0;
  let rotY = 0, rotX = -0.2;
  let waveAlpha = 0, cubeAlpha = 0;   // feature blends for the wave / cube models

  const proj = { x: new Float32Array(N), y: new Float32Array(N), z: new Float32Array(N), s: new Float32Array(N) };
  const order = Array.from({ length: N }, (_, i) => i);

  function render() {
    ctx.clearRect(0, 0, w, h);
    // Continuous 3D turntable wobble — keeps all three models (incl. the flat
    // shield emblem) readable, instead of a full spin that turns them edge-on.
    if (!reduceMotion) {
      t += 0.0045;
      rotY = Math.sin(t) * 0.62;
      rotX = -0.16 + Math.cos(t * 0.8) * 0.12;
    }
    const cosY = Math.cos(rotY), sinY = Math.sin(rotY);
    const cosX = Math.cos(rotX), sinX = Math.sin(rotX);

    // rotate + project every particle (wave model adds a travelling sine in y)
    for (let i = 0; i < N; i++) {
      const p = particles[i];
      let py = p.y;
      if (waveAlpha > 0.001) py += waveAlpha * 0.33 * Math.sin(p.x * 5.5 + p.z * 2.0 - t * 8);
      const rx = p.x * cosY + p.z * sinY;
      const rz = -p.x * sinY + p.z * cosY;
      const ry = py * cosX - rz * sinX;
      const rz2 = py * sinX + rz * cosX;
      const sc = PERSP / (PERSP - rz2);
      proj.x[i] = cx + rx * R * sc;
      proj.y[i] = cy + ry * R * sc;
      proj.z[i] = rz2;
      proj.s[i] = sc;
    }

    // draw back-to-front; the cube model cross-fades dots into 0/1 glyphs
    order.sort((i, j) => proj.z[i] - proj.z[j]);
    const textMode = cubeAlpha;
    if (textMode > 0.02) { ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; }
    let lastFont = '';
    for (let o = 0; o < N; o++) {
      const i = order[o];
      const p = particles[i];
      const depth = (proj.z[i] + 1) / 2;           // 0 (back) .. 1 (front)
      const size = Math.max(0.5, p.baseR * proj.s[i] * (0.55 + depth * 0.9));
      const alpha = 0.38 + depth * 0.62;
      const rgb = p.gold ? '190,163,77' : '196,208,232';
      const tint = p.gold ? 1 : 0.8;
      if (textMode < 0.98) {
        ctx.fillStyle = `rgba(${rgb},${alpha * tint * (1 - textMode)})`;
        ctx.beginPath();
        ctx.arc(proj.x[i], proj.y[i], size, 0, Math.PI * 2);
        ctx.fill();
      }
      if (textMode > 0.02) {
        const fs = Math.max(7, size * 4.6) | 0;
        const fontStr = `${fs}px ui-monospace, Menlo, Consolas, monospace`;
        if (fontStr !== lastFont) { ctx.font = fontStr; lastFont = fontStr; }
        ctx.fillStyle = `rgba(${rgb},${alpha * tint * textMode})`;
        ctx.fillText(p.bit, proj.x[i], proj.y[i]);
      }
    }
    requestAnimationFrame(render);
  }
  render();

  // Morph the cloud to a model index; anime.js drives the per-dot transition.
  let currentIdx = 0;
  function morphTo(idx) {
    if (idx === currentIdx) return;
    currentIdx = idx;
    const target = MODELS[idx].pts;
    particles.forEach((p, i) => { p.tx = target[i][0]; p.ty = target[i][1]; p.tz = target[i][2]; });
    const name = NAMES[idx];
    const wantWave = name === 'wave' ? 1 : 0;
    const wantCube = name === 'cube' ? 1 : 0;
    if (reduceMotion) {
      particles.forEach(p => { p.x = p.tx; p.y = p.ty; p.z = p.tz; });
      waveAlpha = wantWave; cubeAlpha = wantCube;
      return;
    }
    animate(particles, {
      x: p => p.tx, y: p => p.ty, z: p => p.tz,
      duration: 2200,
      delay: stagger(1.4),
      ease: 'inOutQuad',
    });
    const wh = { v: waveAlpha };
    animate(wh, { v: wantWave, duration: 1400, ease: 'inOutQuad', onUpdate: () => { waveAlpha = wh.v; } });
    const ch = { v: cubeAlpha };
    animate(ch, { v: wantCube, duration: 1400, ease: 'inOutQuad', onUpdate: () => { cubeAlpha = ch.v; } });
  }

  // Cycle models on a timer (globe → wave → cube → …).
  if (!reduceMotion) {
    let idx = 0;
    setInterval(() => { idx = (idx + 1) % MODELS.length; morphTo(idx); }, 4200);
  }
}

/* ---------------- Hero text reveal ---------------- */
function heroReveal() {
  const items = document.querySelectorAll('.hero__inner .reveal');
  if (reduceMotion) { items.forEach(i => i.classList.add('is-visible')); return; }
  items.forEach(i => i.classList.add('is-visible'));
  animate('.hero__inner .reveal', {
    opacity: [0, 1],
    translateY: [24, 0],
    duration: 800,
    delay: stagger(120, { start: 150 }),
    ease: 'out(3)',
  });
}

/* ---------------- Reveal-on-scroll ---------------- */
function revealOnScroll() {
  const els = document.querySelectorAll('.reveal:not(.hero__inner .reveal)');
  if (reduceMotion) { els.forEach(e => e.classList.add('is-visible')); return; }
  const obs = new IntersectionObserver((entries) => {
    entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); } });
  }, { threshold: 0.15 });
  els.forEach(e => obs.observe(e));
}

/* ---------------- Nav (scroll state + mobile toggle) ---------------- */
function nav() {
  const navEl = document.getElementById('nav');
  const onScroll = () => navEl.classList.toggle('is-scrolled', window.scrollY > 40);
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  const toggle = document.getElementById('navToggle');
  const links = document.getElementById('navLinks');
  toggle.addEventListener('click', () => {
    const open = links.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(open));
  });
  links.querySelectorAll('a').forEach(a => a.addEventListener('click', () => {
    links.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
  }));
}

document.addEventListener('DOMContentLoaded', () => {
  const y = document.getElementById('year');
  if (y) y.textContent = new Date().getFullYear();
  nav();
  heroReveal();
  revealOnScroll();
  initParticles();
});
